/**
 * FinancialsArea — "the money clauses this contract will print", in the instrument's own order.
 *
 * THIS IS THE ONE CANVAS THAT GETS THE INLINE CORE IN PR2, and the reason is not that the area is
 * important — it is that these four numbers are READ INSIDE THE SENTENCES THEY PRINT AS. A selling
 * price in a labelled box is a database column; the same number sitting in "The Owner agrees to
 * sell the vehicle … for GHS 185,000.00" is a promise somebody is about to sign. Every admin
 * mistake this area exists to prevent is a mistake about what the driver will read.
 *
 * THE ORDER IS THE INSTRUMENT'S, NOT OURS: Cash price, Hire-purchase price, Deposit credited,
 * Weekly Instalment, then the clause 2.3 overdue interest. The first four are "The Money"
 * particulars rows of the signed Schedule and carry its own row labels rather than clause numbers;
 * the rate keeps a number because clause 2.3 states it in a sentence. The cash price leads because
 * the Schedule's particulars lead with it, and it is the one figure on this canvas that is not
 * about what the driver pays: the DIFFERENCE between it and the price below it is the charge for
 * credit, which a hire purchase agreement has to state before it is made. Deposit credited is a
 * READ-ONLY MIRROR — the deposit is recorded where the money is, one area up, and two editors for
 * one figure is how the sum on the agreement and the sum in the bank drift apart.
 *
 * CLAUSE 2.3 IS A CHECK, NOT A FILL. `contracts.overdue_interest_rate` is NOT NULL DEFAULT 365,
 * so it can never be missing; it prints on three separate instruments; and changing it while a
 * document is frozen leaves that document's TEXT quoting the old rate while the overdue engine
 * charges the new one. So it is shown, sourced and explained here — and the edit affordance is
 * open only while nothing has frozen, which `../hazards` decides and a truth table pins.
 *
 * THE PRICE AND THE INSTALMENT CARRY THE SAME GUARD, BY THE SAME RULE. §A5.3 names the case by
 * hand — "any money change once an agreement exists" — and it is the same frozen-text rule
 * underneath: once the hire purchase agreement's markdown is frozen it quotes a price and a
 * weekly figure the contract may no longer carry, and `agreement_freeze_if_needed` refuses the
 * next signing link over exactly that. So the commit routes through the hazard dialog, which shows
 * the row before and after, says what the change costs, and then voids and reissues. Nothing
 * frozen, nothing to ask: the ordinary save is the ordinary save.
 *
 * NO AUTOSAVE, ONE COMMIT PER BLOCK, AND NO TOAST. A dirty block marks itself in the margin and
 * in the manifest ("Draft, not saved" — which never counts as progress); the commit bar is the
 * only thing that writes; a refusal is shown verbatim where it was typed and the typed value stays
 * put. `inlineEditReducer` holds all four of those rules and is unit-tested.
 */

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';

import { formatBusinessDate, formatCurrency, parseBusinessDay } from '@/lib/format';
import { cn } from '@/lib/utils';
import { createAgreement, sweepOrphans, voidAgreement } from '@/lib/agreements/api';
import type { ContractAgreementsModel } from '@/components/agreements/AgreementSlotList';
import VoidReissueDialog from '@/components/agreements/VoidReissueDialog';

import { blockKeyHandler } from '../inline/blockKeys';
import CommitBar from '../inline/CommitBar';
import {
    changedFields,
    initialInlineState,
    inlineEditReducer,
    isDirty,
    type FieldValues,
} from '../inline/inlineEditReducer';
import { QuietInput } from '../inline/QuietField';
import {
    saveCashPrice,
    saveContractInstalment,
    saveManagementRate,
    saveOverdueRate,
    saveSalePrice,
} from '../data/financials';
import type { ContractDeposit } from '../data/depositShapes';
import {
    attributeRefusal,
    CLAUSE_REFUSALS,
    MANAGEMENT_RATE_REFUSALS,
    OVERDUE_RATE_REFUSALS,
} from '../data/refusalFields';
import HazardDialog from '../dossier/HazardDialog';
import { parseAgreementError } from '@/lib/agreements/constants';
import {
    instrumentsFromSlots,
    moneyChangeControl,
    overdueRateControl,
    partnerScheduleMoneyNote,
} from '../hazards';
import { commitMoneyChange } from '../moneyCommit';
import type { SetupArea } from '../steps';
import { Block, Blocks, Hint, LinkLike, Rec, Rows } from './blocks';
import { dailyRateText, derivedConsequences, parseAmount } from './financialsMath';

interface Props {
    contractId: number;
    contract: {
        cash_price?: number | null;
        sold_at?: number | null;
        management_rate?: number | null;
        overdue_interest_rate?: number | null;
    } | null;
    instalment: { id: number; amount: number | null; starts_at: string | null } | null;
    deposits: ContractDeposit[] | undefined;
    /** The insurance loan, mirrored here as one read-only line. Never edited here. */
    insuranceLoan: { amount: number | null; interest_rate: number | null } | null;
    /** The latest `contract_updates` status. Past DRAFT clause 2.3 is read-only. */
    contractStatus: string | undefined;
    /** The page's one signing model — read for the instruments clause 2.3 prints on. */
    agreements: ContractAgreementsModel;
    onGoToArea: (area: SetupArea) => void;
    onSuccess: () => void;
}

/** The rate is NOT NULL DEFAULT 365 in SQL; this is only what to print if a read ever lies. */
const DEFAULT_OVERDUE_RATE = 365;

function toField(value: number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value);
}

/**
 * A DATE column (`contract_instalments.starts_at`) as the 'yyyy-MM-dd' the `<input type="date">`
 * edits. Parsed from its own parts and re-serialised, so the field holds the day the column holds:
 * `new Date('2026-08-01')` is UTC MIDNIGHT, and reading local parts back off it hands New York
 * '2026-07-31' — which the Schedule's Expected completion is then derived from, and which an
 * untouched save would write back.
 */
function dateField(value: string | null | undefined): string {
    const day = parseBusinessDay(value);
    return day ? format(day, 'yyyy-MM-dd') : '';
}

/** A typed figure as the clause prints it: `185,000.00`, or the em-dash the blank renders as. */
function clauseAmount(typed: string): string {
    const parsed = parseAmount(typed);
    return parsed === null
        ? '———'
        : parsed.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * A typed date as the canvas prints it — parsed from its own parts for the same reason `dateField`
 * is. THIS ONE REACHES THE DOCUMENT: the Schedule no longer states the first-instalment date
 * itself, but `contract.expected_completion_date` is derived from it and IS read inside the
 * sentence the driver signs, so a day-early render west of Greenwich is a promise about the wrong
 * Monday.
 */
function clauseDate(typed: string): string {
    const day = parseBusinessDay(typed);
    return day ? format(day, 'MMM d, yyyy') : '———';
}

/** The Schedule's money particulars as one sentence, for the hazard dialog's before and after. */
function moneySentence(values: FieldValues): string {
    return (
        `…cash price GHS ${clauseAmount(values.cashPrice)}, for GHS ${clauseAmount(values.price)}, ` +
        `payable by weekly instalments of GHS ${clauseAmount(values.instalmentAmount)} ` +
        `beginning ${clauseDate(values.instalmentStart)}…`
    );
}

/**
 * What the changed fields are called in the diff, in the instrument's own vocabulary.
 *
 * THE SCHEDULE'S ROW LABELS, NOT CLAUSE NUMBERS. The restructured hire purchase agreement states
 * its money as a particulars table; 8.1 and 8.3 name nothing on the page any more.
 */
const CLAUSE_DIFF_LABELS: Record<string, string> = {
    cashPrice: 'Cash price',
    price: 'Vehicle Purchase Price',
    instalmentAmount: 'Weekly Instalment',
    instalmentStart: 'First instalment',
};

/**
 * A field-scoped refusal, under the clause that holds the field (§A4).
 *
 * IT CARRIES THE ID `QuietInput` ALREADY POINTS AT. The inline variant lives inside a `<p>` and
 * cannot open a row of its own, so it names `${id}-err` in `aria-errormessage` and leaves the
 * element to whoever draws the clause — which is this file.
 */
function ClauseError({ id, message }: { id: string; message: string | undefined }) {
    if (!message) return null;
    return (
        <p className="sd-f-err" id={`${id}-err`}>
            {message}
        </p>
    );
}

/** Whether anything on this block's refusal already has a home under a field. */
function hasFieldErrors(fieldErrors: Record<string, string>): boolean {
    return Object.keys(fieldErrors).length > 0;
}

export default function FinancialsArea({
    contractId,
    contract,
    instalment,
    deposits,
    insuranceLoan,
    contractStatus,
    agreements,
    onGoToArea,
    onSuccess,
}: Props) {
    const rows = deposits ?? [];
    // NO DEPOSIT AND A LINKED ZERO ARE TWO DIFFERENT ANSWERS [D-B41], and this mirror gives the
    // one the Schedule would print. `agreement_merge_data` resolves `contract.deposit_amount` to
    // '0.00' where the contract carries no deposit row — so GH₵0.00 IS the figure, and drawing the
    // `———` there would report an unrenderable clause on a contract that renders. Rows that exist
    // and sum to zero still resolve NULL, so that case keeps the missing mark and the manifest's
    // deposit row stays undone beside it.
    const summedDeposits = rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
    const depositTotal = rows.length === 0 ? 0 : summedDeposits > 0 ? summedDeposits : null;
    const depositPaidOn = rows[0]?.transaction_at ?? null;
    const overdueRate = contract?.overdue_interest_rate ?? DEFAULT_OVERDUE_RATE;

    // ── the Money particulars, one block, one commit ─────────────────────────
    const clauseServer = useMemo<FieldValues>(
        () => ({
            cashPrice: toField(contract?.cash_price),
            price: toField(contract?.sold_at),
            instalmentAmount: toField(instalment?.amount),
            instalmentStart: dateField(instalment?.starts_at),
        }),
        [contract?.cash_price, contract?.sold_at, instalment?.amount, instalment?.starts_at],
    );

    const [clauses, dispatchClauses] = useReducer(inlineEditReducer, clauseServer, initialInlineState);

    /** The board's slots as facts, read by both money rules below. */
    const instruments = useMemo(
        () => instrumentsFromSlots(agreements.slots),
        [agreements.slots],
    );

    /**
     * WHETHER SAVING THE PRICE OR THE INSTALMENT HAS TO ASK FIRST, decided in `../hazards` where a
     * table pins it: a hire purchase agreement whose text has frozen quotes these two figures, and
     * moving the columns underneath it is what leaves a signed document describing terms nobody
     * agreed to.
     */
    const moneyControl = useMemo(() => moneyChangeControl({ instruments }), [instruments]);

    /**
     * WHAT THE SERVER DOES TO THE VEHICLE SCHEDULE WHEN THIS BLOCK SAVES [D-B48].
     *
     * REPORTED, AND GATED ONLY IN `commitMoneyChange` [D-B59], and it is a separate reading from
     * `moneyControl` for two reasons. The withdrawal is the DATABASE's —
     * `trg_contracts_void_partner_schedule` deletes the document and the unsealed referent when
     * `cash_price`, `sold_at`, `management_rate` or `vehicle_id` moves on a DRAFT contract — so it
     * must not enter `moneyControl.voidable`, which is the list this screen voids by hand through a
     * door that deliberately keeps the referent. And the hazard confirm cannot
     * carry it alone: `moneyChangeControl` fires on a FROZEN hire purchase agreement, and the ruled
     * order puts the partner's signature FIRST, so on the ordinary DRAFT contract the schedule
     * exists while no confirm is opened at all. The sentence therefore stands on the canvas.
     */
    const scheduleNote = useMemo(
        () => partnerScheduleMoneyNote({ status: contractStatus ?? 'DRAFT', instruments }),
        [contractStatus, instruments],
    );

    const [moneyConfirming, setMoneyConfirming] = useState(false);

    /**
     * THE SERVER'S REFUSAL, IN THE DIALOG THAT ASKED FOR IT. The confirm no longer closes on a
     * failure, so the sentence needs a home there; the clause's own `fieldErrors` still carry it
     * under the field it names, for the plain save that never opened a dialog at all.
     */
    const [moneyRefusal, setMoneyRefusal] = useState<string | null>(null);

    /**
     * THE READ MOVED; THE DRAFT DOES NOT. Three of the underlying queries are `staleTime: 0` and
     * refetch on every window focus, and the forms this replaces re-seeded their `useState` from
     * exactly this effect — which is what silently discarded whatever an admin was typing.
     */
    useEffect(() => {
        dispatchClauses({ type: 'sync', server: clauseServer });
    }, [clauseServer]);

    const commitClauses = useMutation({
        /**
         * THE DOCUMENT FIRST, THE MONEY SECOND, THE REISSUE LAST — the vehicle assignment's order,
         * for the vehicle assignment's reasons (`VehicleArea`'s `AssignmentBlock`).
         *
         * A VOID THAT IS GOING TO BE REFUSED HAS TO BE REFUSED BEFORE THE COLUMNS MOVE. Contract
         * #319 carries `sold_at` 211,285.717 while its signed hire purchase document goes on
         * quoting 266,185.72 — the money was written, the void was then refused, and the two were
         * never put back together. Voiding also releases the arc's live-document slot, which is
         * what lets the create that follows produce a new document rather than hand back the old
         * one; and the reissue renders from columns the write between them has moved [D-B42].
         *
         * THE PRE-FLIGHT BUYS BACK WHAT THE OLD ORDER GAVE AWAY. Writing first meant a typo was
         * refused before any document could be lost; `commitMoneyChange` reads the same three
         * refusals the writes raise, before the first void, and the order itself is unit-tested.
         * It reads `scheduleNote` too [D-B59]: an executed Vehicle Schedule refuses a cash-price or
         * selling-price write, and that refusal has to land before the void rather than after it.
         */
        mutationFn: (plan: { reason: string } | null) =>
            commitMoneyChange(
                {
                    contractId,
                    existingInstalmentId: instalment?.id ?? null,
                    draft: {
                        cashPrice: clauses.draft.cashPrice,
                        price: clauses.draft.price,
                        instalmentAmount: clauses.draft.instalmentAmount,
                        instalmentStart: clauses.draft.instalmentStart,
                    },
                    changed: changedFields(clauses),
                    touched: clauses.touched,
                    voidable: plan ? moneyControl.voidable : [],
                    reason: plan?.reason ?? null,
                    scheduleNote,
                },
                {
                    saveCashPrice,
                    saveSalePrice,
                    saveContractInstalment,
                    voidAgreement,
                    createAgreement,
                    // SIXTH, AND FIRED LAST BY THE ORCHESTRATOR [D-B44]. The sweep's position —
                    // after every create, once per gesture, only where something was withdrawn —
                    // is pinned by `moneyCommit.test.ts`'s call log rather than by this line.
                    sweepOrphans,
                },
            ),
        onSuccess: (_data, plan) => {
            dispatchClauses({
                type: 'committed',
                server: clauses.draft,
                at: format(new Date(), 'MMM d, yyyy · HH:mm'),
            });
            setMoneyConfirming(false);
            // THE BOARD IS REFRESHED WHETHER OR NOT A DOCUMENT MOVED. The reissue path obviously
            // changes it; a plain save changes it too, because the cash price this block writes is
            // a precondition of the hire purchase agreement — so a save that skipped the refresh
            // would leave the signing board still refusing to generate over the figure it now has.
            agreements.refreshBoard();
            onSuccess();
        },
        /**
         * VERBATIM, AND THE DRAFT STAYS. The three writes throw the sentences an admin can act on
         * ("Enter a valid sale price", "Select a start date"), and PostgREST throws its own — none
         * of them is improved by being paraphrased.
         *
         * THE DIALOG STAYS OPEN, WHICH IS THE PART THAT CHANGED [D-B42]. A refusal here is now
         * about the decision that dialog is holding — the void it asked for — and the retry is the
         * same confirm, so the sentence belongs on the surface that asked for it rather than under
         * a clause on a page the admin has already been returned to. The board is refreshed for the
         * same reason: a void that succeeded before a later failure has already deleted a document
         * the board is still drawing.
         */
        onError: (error: Error) => {
            /*
             * VERBATIM STILL, AND `parseAgreementError` IS WHAT VERBATIM MEANS NOW. It hands back
             * the raw string untouched for anything that is not a `CODE: sentence` — which is all
             * three client-side refusals and PostgREST's own — and strips the code prefix from the
             * ones that are. The one it changes is `PARTNER_SCHEDULE_RECORD_IMMUTABLE`, raised by
             * the trigger that watches `sold_at` [D-B48]: without this an admin read a bare SQL
             * identifier in front of the sentence, and read no instruction at all about where the
             * withdrawal is done.
             */
            const message = parseAgreementError(error).message || 'The save was refused.';
            setMoneyRefusal(message);
            dispatchClauses({
                type: 'failed',
                message,
                fieldErrors: attributeRefusal(CLAUSE_REFUSALS, message),
            });
            agreements.refreshBoard();
        },
    });

    /** The staged commit: ask when a frozen instrument quotes these figures, write when not. */
    function askOrSaveClauses() {
        if (moneyControl.hazard) {
            setMoneyRefusal(null);
            setMoneyConfirming(true);
            return;
        }
        dispatchClauses({ type: 'commit' });
        commitClauses.mutate(null);
    }

    // ── the internal block ───────────────────────────────────────────────────
    const internalServer = useMemo<FieldValues>(
        () => ({ managementRate: toField(contract?.management_rate) }),
        [contract?.management_rate],
    );
    const [internal, dispatchInternal] = useReducer(
        inlineEditReducer,
        internalServer,
        initialInlineState,
    );
    useEffect(() => {
        dispatchInternal({ type: 'sync', server: internalServer });
    }, [internalServer]);

    const commitInternal = useMutation({
        mutationFn: () => saveManagementRate(contractId, internal.draft.managementRate),
        onSuccess: () => {
            dispatchInternal({
                type: 'committed',
                server: internal.draft,
                at: format(new Date(), 'MMM d, yyyy · HH:mm'),
            });
            onSuccess();
        },
        onError: (error: Error) => {
            // THE FEE IS WATCHED BY THE SAME TRIGGER THE PRICE IS, so this save meets the same
            // refusal and is read the same way.
            const message = parseAgreementError(error).message || 'The save was refused.';
            dispatchInternal({
                type: 'failed',
                message,
                fieldErrors: attributeRefusal(MANAGEMENT_RATE_REFUSALS, message),
            });
        },
    });

    // ── clause 2.3 — a check with an edit affordance ─────────────────────────
    /**
     * WHETHER IT CAN BE TYPED INTO AT ALL, decided in `../hazards` where a table can pin it: DRAFT,
     * and no instrument that prints the rate has frozen its text. Past that point the column and
     * somebody's signed copy would say different things, and the row goes read-only with the reason
     * on screen rather than with a disabled input nobody can account for.
     */
    const rateControl = useMemo(
        () =>
            overdueRateControl({
                contractId,
                status: contractStatus ?? 'DRAFT',
                instruments,
            }),
        [contractId, contractStatus, instruments],
    );

    const rateServer = useMemo<FieldValues>(
        () => ({ overdueRate: toField(contract?.overdue_interest_rate ?? DEFAULT_OVERDUE_RATE) }),
        [contract?.overdue_interest_rate],
    );
    const [rate, dispatchRate] = useReducer(inlineEditReducer, rateServer, initialInlineState);
    useEffect(() => {
        dispatchRate({ type: 'sync', server: rateServer });
    }, [rateServer]);

    const [confirming, setConfirming] = useState(false);
    /** The typed rate the confirm will write, held across the dialog. */
    const pendingRate = useRef<string>('');

    const commitRate = useMutation({
        /**
         * THE RATE FIRST, THE DOCUMENT SECOND, THE REISSUE LAST.
         *
         * `saveOverdueRate` refuses a bad figure before it writes anything, so a typo can never cost
         * a document; voiding releases the arc's live-document slot, which is what lets the create
         * that follows produce a new one rather than hand back the old; and the reissue renders from
         * a column this call has already moved.
         */
        mutationFn: async (plan: { reason: string; reissue: boolean } | null) => {
            await saveOverdueRate(contractId, pendingRate.current);
            const slot = rateControl.confirmVia;
            if (!plan || slot?.documentId == null) return;
            await voidAgreement(slot.documentId, plan.reason || null);
            if (plan.reissue) await createAgreement(slot.docType, slot.subjectId);
            // The bytes that void stranded [D-B44], after the reissue and only where a document
            // actually went. This path reaches an executed PDF barely — `overdueRateControl`
            // returns a `confirmVia` only while NOTHING is frozen, so the target is
            // PENDING_REVIEW and owns none — but the void is real, so the sweep is wired the same
            // way it is everywhere else rather than reasoned about per surface. NOT AWAITED: the
            // confirm's spinner is about the void, and a background job must not hold it open.
            sweepOrphans();
        },
        onSuccess: () => {
            dispatchRate({
                type: 'committed',
                server: { overdueRate: pendingRate.current },
                at: format(new Date(), 'MMM d, yyyy · HH:mm'),
            });
            setConfirming(false);
            agreements.refreshBoard();
            onSuccess();
        },
        // The dialog closes so the server's own sentence is read under the clause it refused,
        // which is where every other refusal on this canvas lands.
        onError: (error: Error) => {
            setConfirming(false);
            const message = error.message || 'The save was refused.';
            dispatchRate({
                type: 'failed',
                message,
                fieldErrors: attributeRefusal(OVERDUE_RATE_REFUSALS, message),
            });
        },
    });

    function askOrSaveRate() {
        pendingRate.current = rate.draft.overdueRate;
        if (rateControl.confirmVia) {
            setConfirming(true);
            return;
        }
        dispatchRate({ type: 'commit' });
        commitRate.mutate(null);
    }

    const rateDirty = isDirty(rate);
    /** What the panel prices a missed week at: the typed figure while one is being typed. */
    const liveRate = rateControl.editable ? parseAmount(rate.draft.overdueRate) : overdueRate;
    /** The same figure as the agreements print it — per day, four places. Tracks the keystrokes. */
    const liveDailyRate = dailyRateText(liveRate);

    const derived = derivedConsequences({
        sellingPrice: parseAmount(clauses.draft.price),
        depositTotal,
        instalmentAmount: parseAmount(clauses.draft.instalmentAmount),
        overdueRate: liveRate,
    });

    const clausesDirty = isDirty(clauses);
    const internalDirty = isDirty(internal);
    const changed = changedFields(clauses);

    const clauseKeys = blockKeyHandler(() => dispatchClauses({ type: 'cancel' }));
    const internalKeys = blockKeyHandler(() => dispatchInternal({ type: 'cancel' }));
    const rateKeys = blockKeyHandler(() => dispatchRate({ type: 'cancel' }));

    return (
        <Blocks>
            <Block
                id="b-price"
                title="The Money — price, deposit, instalment"
                unsaved={clausesDirty}
                rejected={clauses.status === 'error' ? 'the save was refused' : undefined}
            >
                <div className="sd-clauses" onKeyDown={clauseKeys}>
                    <div className="sd-clause">
                        <span className="sd-cl-no">Cash price</span>
                        <div>
                            <p className="sd-cl-text">
                                GHS&nbsp;
                                <QuietInput
                                    inline
                                    id="f-cashPrice"
                                    aria-label="Cash price"
                                    inputMode="decimal"
                                    placeholder="———"
                                    value={clauses.draft.cashPrice}
                                    onValueChange={(value) =>
                                        dispatchClauses({ type: 'edit', field: 'cashPrice', value })
                                    }
                                    error={clauses.fieldErrors.cashPrice ?? null}
                                />
                                , the price at which the Vehicle could be bought outright for cash.
                            </p>
                            <ClauseError id="f-cashPrice" message={clauses.fieldErrors.cashPrice} />
                            <p className="sd-cl-sub">
                                {/* IT IS ASKED FOR RATHER THAN WORKED OUT, and the sub-line says so
                                    because the arithmetic is so tempting: the two figures differ by
                                    the charge for credit, so deriving either from the other prints a
                                    contract that charges nothing for it. */}
                                Typed, never derived from the price below — the difference between
                                the two is what this contract charges for credit, and the agreement
                                has to state it before it is made. A hire purchase agreement cannot
                                be generated until it is filled. On a partner’s vehicle it is also
                                the Cash price the partner’s Vehicle Schedule states.
                            </p>
                        </div>
                    </div>

                    <div className="sd-clause">
                        <span className="sd-cl-no">Vehicle Purchase Price</span>
                        <div>
                            <p className="sd-cl-text">
                                GHS&nbsp;
                                <QuietInput
                                    inline
                                    id="f-price"
                                    aria-label="Selling price"
                                    inputMode="decimal"
                                    placeholder="———"
                                    value={clauses.draft.price}
                                    onValueChange={(value) =>
                                        dispatchClauses({ type: 'edit', field: 'price', value })
                                    }
                                    error={clauses.fieldErrors.price ?? null}
                                />
                                {/* The when is the grid's, not a literal in 2.1: the Schedule states
                                    the payment day and the deadline as particulars, each of them
                                    subject to written notice under clause 4.2 of the Terms. */}
                                , payable by the Deposit credited and the Weekly Instalment below, on
                                the payment day and by the deadline the Schedule shows — Monday by
                                noon, unless Primelane gives written notice under Terms 4.2 (clause
                                2.1 of the Schedule).
                            </p>
                            <ClauseError id="f-price" message={clauses.fieldErrors.price} />
                            <p className="sd-cl-sub">
                                The vehicle price alone. Insurance and advances are additive financing —
                                they are never subtracted from it.
                            </p>
                        </div>
                    </div>

                    <div className="sd-clause">
                        <span className="sd-cl-no">Deposit credited</span>
                        <div>
                            <p className="sd-cl-text">
                                {depositTotal === null ? (
                                    <span className="sd-cl-val-missing">———</span>
                                ) : (
                                    <span className="sd-cl-val">{formatCurrency(depositTotal)}</span>
                                )}
                                {depositPaidOn && (
                                    <>
                                        , received{' '}
                                        <span className="sd-cl-val">
                                            {formatBusinessDate(depositPaidOn)}
                                        </span>
                                    </>
                                )}
                            </p>
                            <p className="sd-cl-sub">
                                {/* THE SCHEDULE CREDITS THE DEPOSIT WITHOUT DATING IT. The date is
                                    kept on this canvas because an admin reconciling a payment needs
                                    it; it is named as this app's fact rather than the page's. */}
                                Read-only mirror of the payment recorded in Deposit — the Schedule
                                prints the amount alone.{' '}
                                <LinkLike onClick={() => onGoToArea('deposit')}>Open Deposit</LinkLike>
                            </p>
                        </div>
                    </div>

                    <div className="sd-clause">
                        <span className="sd-cl-no">Weekly Instalment</span>
                        <div>
                            <p className="sd-cl-text">
                                GHS&nbsp;
                                <QuietInput
                                    inline
                                    id="f-instalmentAmount"
                                    aria-label="Weekly instalment"
                                    inputMode="decimal"
                                    placeholder="———"
                                    value={clauses.draft.instalmentAmount}
                                    onValueChange={(value) =>
                                        dispatchClauses({
                                            type: 'edit',
                                            field: 'instalmentAmount',
                                            value,
                                        })
                                    }
                                    error={clauses.fieldErrors.instalmentAmount ?? null}
                                />{' '}
                                each week, beginning{' '}
                                <QuietInput
                                    inline
                                    wide
                                    id="f-instalmentStart"
                                    aria-label="First instalment date"
                                    type="date"
                                    value={clauses.draft.instalmentStart}
                                    onValueChange={(value) =>
                                        dispatchClauses({
                                            type: 'edit',
                                            field: 'instalmentStart',
                                            value,
                                        })
                                    }
                                    error={clauses.fieldErrors.instalmentStart ?? null}
                                />
                                , until the Vehicle Purchase Price and every other amount payable
                                under the Agreement has been paid in full. That amortises{' '}
                                {derived.balance === null ? (
                                    <span className="sd-cl-val-missing">———</span>
                                ) : (
                                    <span className="sd-cl-val">{formatCurrency(derived.balance)}</span>
                                )}
                                , and the Schedule prints the Term and the Expected completion it
                                works out to.
                            </p>
                            <ClauseError
                                id="f-instalmentAmount"
                                message={clauses.fieldErrors.instalmentAmount}
                            />
                            <ClauseError
                                id="f-instalmentStart"
                                message={clauses.fieldErrors.instalmentStart}
                            />
                        </div>
                    </div>
                </div>

                <div className="sd-derived">
                    <div className="sd-derived-h">What this makes true</div>
                    <div className="sd-derived-grid">
                        {derived.cells.map((cell) => (
                            <div key={cell.label}>
                                <div className="sd-dv-k">{cell.label}</div>
                                <div className={cell.empty ? 'sd-dv-v sd-dv-v-dim' : 'sd-dv-v'}>
                                    {cell.value}
                                    {cell.sub && (
                                        <span className="sd-sub"> · {cell.sub}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <p
                        className={
                            derived.alarming ? 'sd-derived-note sd-derived-note-alarm' : 'sd-derived-note'
                        }
                    >
                        {derived.note}
                    </p>
                </div>

                {/* What is left once the field-scoped refusals are under their own clauses. */}
                {clauses.error && !hasFieldErrors(clauses.fieldErrors) && (
                    <p className="sd-f-err">{clauses.error}</p>
                )}

                {scheduleNote && <Hint className="mt-3">{scheduleNote.sentence}</Hint>}

                {clausesDirty && (
                    <CommitBar
                        saving={commitClauses.isPending}
                        hint={
                            moneyControl.hint ??
                            (changed.length > 1
                                ? `${changed.length} fields commit together`
                                : 'One commit for the whole block')
                        }
                        onSave={askOrSaveClauses}
                        onDiscard={() => dispatchClauses({ type: 'cancel' })}
                    />
                )}
                {clauses.savedAt && !clausesDirty && (
                    <p className="sd-prov">Saved {clauses.savedAt}</p>
                )}

                {/*
                 * THE CONFIRM IS THE DOSSIER'S OWN HAZARD DIALOG, not the board's void ceremony.
                 * What is being decided here is a change to the CLAUSE — so the body has to be the
                 * clause, struck through as it stands and in brand ink as it will read, with the
                 * figures beside it. The void is a consequence of that decision rather than the
                 * decision itself, which is why it does not ask for an evidence reason: the reason
                 * IS the diff, and it is written into the void from the fields that moved.
                 */}
                <HazardDialog
                    open={moneyConfirming}
                    onOpenChange={(open) => !open && setMoneyConfirming(false)}
                    title={`Change the money on contract #${contractId}`}
                    consequence={
                        scheduleNote
                            ? `${moneyControl.consequence ?? ''} ${scheduleNote.sentence}`.trim()
                            : (moneyControl.consequence ?? '')
                    }
                    fragment={{
                        old: moneySentence(clauses.server),
                        now: moneySentence(clauses.draft),
                    }}
                    diffs={changed
                        .filter((field) => CLAUSE_DIFF_LABELS[field])
                        .map((field) => ({
                            label: CLAUSE_DIFF_LABELS[field],
                            old:
                                field === 'instalmentStart'
                                    ? clauseDate(clauses.server[field] ?? '')
                                    : clauseAmount(clauses.server[field] ?? ''),
                            now:
                                field === 'instalmentStart'
                                    ? clauseDate(clauses.draft[field] ?? '')
                                    : clauseAmount(clauses.draft[field] ?? ''),
                        }))}
                    confirmLabel={
                        moneyControl.voidable.length > 0
                            ? 'Save and void the agreement'
                            : 'Save the change anyway'
                    }
                    cancelLabel={
                        moneyControl.voidable.length > 0
                            ? 'Keep the agreement and cancel'
                            : 'Leave the clauses as they are'
                    }
                    pending={commitClauses.isPending}
                    error={moneyRefusal}
                    onConfirm={() => {
                        setMoneyRefusal(null);
                        dispatchClauses({ type: 'commit' });
                        commitClauses.mutate(
                            moneyControl.voidable.length === 0
                                ? null
                                : {
                                      reason: `The Money particulars changed: ${moneySentence(clauses.draft)}`,
                                  },
                        );
                    }}
                />
            </Block>

            <Block
                id="b-overdue"
                title="Interest on a late payment"
                aside={rateControl.editable ? 'check, rarely change' : 'check, do not fill'}
                unsaved={rateDirty}
                rejected={rate.status === 'error' ? 'the save was refused' : undefined}
            >
                <div className="sd-clauses" onKeyDown={rateKeys}>
                    <div className="sd-clause">
                        <span className="sd-cl-no">Interest on a late payment</span>
                        <div>
                            <p className="sd-cl-text">
                                Interest at{' '}
                                {rateControl.editable ? (
                                    <>
                                        <QuietInput
                                            inline
                                            className="sd-qf-w5"
                                            id="f-overdueRate"
                                            aria-label="Overdue interest rate, per cent per annum"
                                            inputMode="decimal"
                                            placeholder="———"
                                            value={rate.draft.overdueRate}
                                            onValueChange={(value) =>
                                                dispatchRate({
                                                    type: 'edit',
                                                    field: 'overdueRate',
                                                    value,
                                                })
                                            }
                                            error={rate.fieldErrors.overdueRate ?? null}
                                        />
                                        <span className="sd-cl-val">% per annum</span>
                                    </>
                                ) : (
                                    <span className="sd-cl-val">{overdueRate}% per annum</span>
                                )}{' '}
                                is payable on any amount that is not paid when it falls due, for each
                                day it remains unpaid — which is{' '}
                                {liveDailyRate === null ? (
                                    <span className="sd-cl-val-missing">———</span>
                                ) : (
                                    <span className="sd-cl-val">
                                        {liveDailyRate} per cent per day
                                    </span>
                                )}
                                , the figure the agreements actually print.
                            </p>
                            <ClauseError
                                id="f-overdueRate"
                                message={rate.fieldErrors.overdueRate}
                            />
                            <p className="sd-cl-sub">
                                {/* THE STORED FIGURE IS ANNUAL AND THE PRINTED ONE IS DAILY, which is
                                    the whole reason the daily rate is shown above rather than left for
                                    an admin to divide. The schedules make the printed figure operative,
                                    so it has to be checkable here. */}
                                This rate can never be missing — the column is set to{' '}
                                {DEFAULT_OVERDUE_RATE}% the moment a contract is created. It is stored
                                per annum and printed per day, on the hire purchase schedule and both
                                advance schedules alike, and the overdue engine charges it daily over a
                                365-day year.{' '}
                                {rateControl.editable
                                    ? 'Check it against what was agreed; change it only if that figure is wrong.'
                                    : 'Read it; do not fill it.'}
                            </p>
                        </div>
                    </div>
                </div>

                {rate.error && !hasFieldErrors(rate.fieldErrors) && (
                    <p className="sd-f-err">{rate.error}</p>
                )}

                {rateDirty && (
                    <CommitBar
                        saving={commitRate.isPending}
                        hint={
                            rateControl.confirmVia
                                ? 'A live hire purchase agreement exists · saving asks first'
                                : 'One commit for the whole block'
                        }
                        onSave={askOrSaveRate}
                        onDiscard={() => dispatchRate({ type: 'cancel' })}
                    />
                )}
                {rate.savedAt && !rateDirty && <p className="sd-prov">Saved {rate.savedAt}</p>}

                {!rateControl.editable && rateControl.reason && (
                    <Hint className="mt-3">{rateControl.reason}</Hint>
                )}

                {/*
                 * THE CEREMONY IS THE BOARD'S OWN. Voiding is evidence collection — the reason it
                 * takes is the audit trail the records list shows in the document's place — so it
                 * happens in the dialog that already knows how to ask for one, not in a second
                 * confirm written here that would drift from it.
                 */}
                {rateControl.confirmVia && (
                    <VoidReissueDialog
                        open={confirming}
                        onOpenChange={(open) => !open && setConfirming(false)}
                        documentTitle={rateControl.confirmVia.title}
                        referenceNo={rateControl.confirmVia.referenceNo ?? ''}
                        requiresReason={rateControl.confirmVia.status === 'COMPLETED'}
                        isPending={commitRate.isPending}
                        onConfirm={(reason, reissue) => {
                            dispatchRate({ type: 'commit' });
                            commitRate.mutate({
                                reason:
                                    reason ||
                                    `Overdue interest rate changed to ${pendingRate.current}% per annum`,
                                reissue,
                            });
                        }}
                    />
                )}
            </Block>

            <Block
                id="b-internal"
                title="Internal"
                aside="never printed"
                unsaved={internalDirty}
                rejected={internal.status === 'error' ? 'the save was refused' : undefined}
            >
                <Rows>
                    <div
                        className={cn(
                            'sd-fld',
                            internalDirty && 'sd-dirty',
                            internal.fieldErrors.managementRate && 'sd-errored',
                        )}
                        onKeyDown={internalKeys}
                    >
                        <label className="sd-fld-k" htmlFor="f-managementRate">
                            Management rate
                        </label>
                        <div className="flex min-w-0 items-center gap-2">
                            <QuietInput
                                id="f-managementRate"
                                inputMode="decimal"
                                placeholder="—"
                                format="% of the vehicle allocation"
                                value={internal.draft.managementRate}
                                onValueChange={(value) =>
                                    dispatchInternal({ type: 'edit', field: 'managementRate', value })
                                }
                                error={internal.fieldErrors.managementRate ?? null}
                            />
                        </div>
                        <ClauseError
                            id="f-managementRate"
                            message={internal.fieldErrors.managementRate}
                        />
                        {internal.error && !hasFieldErrors(internal.fieldErrors) && (
                            <p className="sd-f-err">{internal.error}</p>
                        )}
                    </div>
                    <Rec
                        label="Insurance financed"
                        value={
                            insuranceLoan?.amount == null ? null : (
                                <span className="sd-num">
                                    {formatCurrency(insuranceLoan.amount)} at{' '}
                                    {insuranceLoan.interest_rate ?? 0}%
                                </span>
                            )
                        }
                        sub={
                            <>
                                recorded in Insurance ·{' '}
                                <LinkLike onClick={() => onGoToArea('insurance')}>
                                    Open Insurance
                                </LinkLike>
                            </>
                        }
                        empty="Not financed"
                    />
                </Rows>
                <Hint className="mt-3">
                    Primelane&rsquo;s own margin — the remainder of the allocation goes to the partner.
                    It reaches no driver-facing surface, but it is NOT unprinted any more: the Vehicle
                    Schedule states it as the management fee and divides the partner&rsquo;s weekly
                    return out of it, so on a partner&rsquo;s vehicle this figure is a term of a signed
                    instrument rather than an internal note.
                </Hint>
                {scheduleNote && <Hint className="mt-3">{scheduleNote.sentence}</Hint>}
                {internalDirty && (
                    <CommitBar
                        saving={commitInternal.isPending}
                        hint="Internal only"
                        onSave={() => commitInternal.mutate()}
                        onDiscard={() => dispatchInternal({ type: 'cancel' })}
                    />
                )}
                {internal.savedAt && !internalDirty && (
                    <p className="sd-prov">Saved {internal.savedAt}</p>
                )}
            </Block>
        </Blocks>
    );
}
